import React, { useMemo } from 'react';
import { makeStyles, shorthands, tokens } from '@fluentui/react-components';
import type { DataModelRelationship, ViewRelationshipItem } from '../../api/dataModelsService';

// ─── Styles ──────────────────────────────────────────────────────────────────

const useStyles = makeStyles({
  tree: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    fontFamily: tokens.fontFamilyMonospace,
    fontSize: '13px',
    color: tokens.colorNeutralForeground2,
    lineHeight: '22px',
  },
  root: {
    fontWeight: 600,
    color: tokens.colorNeutralForeground1,
  },
  branch: {
    ...shorthands.padding('0', '0', '0', '16px'),
    color: tokens.colorNeutralForeground2,
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '8px',
    ...shorthands.padding('20px', '0'),
    textAlign: 'center',
    color: tokens.colorNeutralForeground3,
    fontSize: '13px',
  },
});

// ─── View-level relationship tree ────────────────────────────────────────────

interface ViewRelationshipTreeProps {
  relationships: ViewRelationshipItem[];
  currentViewName: string;
  entityDisplayName: (name: string) => string;
}

/** Format key labels: "Owner, CreatedBy" from ["OwnerId", "CreatedById"] */
function formatKeys(keys: string[]): string {
  return keys.map((k) => k.replace(/Id$/i, '').replace(/([A-Z])/g, ' $1').trim()).join(', ');
}

export const ViewRelationshipTreeComponent: React.FC<ViewRelationshipTreeProps> = ({
  relationships,
  currentViewName,
  entityDisplayName,
}) => {
  const styles = useStyles();

  const grouped = useMemo(() => {
    const bySource = new Map<string, Map<string, { type: string; keys: string[] }>>();
    for (const rel of relationships) {
      const from = rel.fromViewName;
      const to = rel.toViewName;
      const keyLabel = rel.columnPairs?.length
        ? rel.columnPairs.map(pair => `${pair.from_column} → ${pair.to_column}`).join(' AND ')
        : rel.fromKey;
      if (!bySource.has(from)) bySource.set(from, new Map());
      const targets = bySource.get(from)!;
      if (!targets.has(to)) {
        targets.set(to, { type: rel.relationshipType, keys: [keyLabel] });
      } else {
        const existing = targets.get(to)!;
        if (!existing.keys.includes(keyLabel)) {
          existing.keys.push(keyLabel);
        }
      }
    }
    return bySource;
  }, [relationships]);

  const { factSections, topLevelLinks } = useMemo(() => {
    const sections: { sourceName: string; dimensions: { name: string; keys: string[] }[] }[] = [];
    const links: { fromName: string; toName: string; fromKey: string; toKey: string }[] = [];

    for (const [source, targets] of grouped.entries()) {
      const dimensions: { name: string; keys: string[] }[] = [];
      for (const [target, info] of targets.entries()) {
        if (info.type === 'multi_fact') {
          const origRel = relationships.find(
            (r) => r.fromViewName === source && r.toViewName === target && r.relationshipType === 'multi_fact'
          );
          links.push({
            fromName: source,
            toName: target,
            fromKey: origRel?.fromKey || '',
            toKey: origRel?.toKey || '',
          });
        } else {
          dimensions.push({ name: target, keys: info.keys });
        }
      }
      if (dimensions.length > 0) {
        sections.push({ sourceName: source, dimensions });
      }
    }
    return { factSections: sections, topLevelLinks: links };
  }, [grouped, relationships]);

  const hasLinks = topLevelLinks.length > 0;

  return (
    <div className={styles.tree}>
      <span className={styles.root}>{currentViewName}</span>
      {factSections.map((section, si) => {
        const isLastSection = si === factSections.length - 1 && !hasLinks;
        return (
          <div key={si}>
            <div className={styles.branch} style={{ fontWeight: 600, color: tokens.colorNeutralForeground1 }}>
              {isLastSection ? '└── ' : '├── '}{entityDisplayName(section.sourceName)}
            </div>
            {section.dimensions.map((dim, di) => {
              const isLast = di === section.dimensions.length - 1;
              return (
                <div key={`d-${di}`} className={styles.branch} style={{ paddingLeft: '28px' }}>
                  {isLast ? '└── ' : '├── '}{entityDisplayName(dim.name)}
                  {dim.keys.length > 0 && (
                    <span style={{ opacity: 0.5, fontSize: '11px', marginLeft: '6px' }}>
                      ({formatKeys(dim.keys)})
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        );
      })}
      {hasLinks && (
        <div style={{ marginTop: '4px' }}>
          <div className={styles.branch} style={{ fontSize: '10px', color: tokens.colorNeutralForeground4, paddingLeft: '4px' }}>
            linked facts:
          </div>
          {topLevelLinks.map((link, li) => {
            const isLast = li === topLevelLinks.length - 1;
            return (
              <div key={`link-${li}`} className={styles.branch} style={{ paddingLeft: '4px' }}>
                {isLast ? '└── ' : '├── '}{entityDisplayName(link.fromName)} → {entityDisplayName(link.toName)}
                {link.fromKey && link.toKey && (
                  <span style={{ opacity: 0.5, fontSize: '11px', marginLeft: '6px' }}>
                    ({link.fromKey} → {link.toKey})
                  </span>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ─── Dataset-level relationship tree (fallback) ──────────────────────────────

interface DatasetRelationshipTreeProps {
  relationships: DataModelRelationship[];
  baseTables: string[];
  isCrossDomain: boolean;
  viewName: string;
  entityDisplayName: (name: string) => string;
}

export const DatasetRelationshipTree: React.FC<DatasetRelationshipTreeProps> = ({
  relationships,
  baseTables,
  isCrossDomain,
  viewName,
  entityDisplayName,
}) => {
  const styles = useStyles();

  const tree = useMemo(() => {
    const roots = new Set(baseTables.map((t) => t.toLowerCase()));
    const grouped = new Map<string, string[]>();

    for (const rel of relationships) {
      const fromName = rel.fromDataset.name;
      const toName = rel.toDataset.name;
      if (roots.has(fromName.toLowerCase())) {
        const children = grouped.get(fromName) || [];
        if (!children.includes(toName)) children.push(toName);
        grouped.set(fromName, children);
      } else if (roots.has(toName.toLowerCase())) {
        const children = grouped.get(toName) || [];
        if (!children.includes(fromName)) children.push(fromName);
        grouped.set(toName, children);
      }
    }
    return grouped;
  }, [relationships, baseTables]);

  if (isCrossDomain) {
    return (
      <div className={styles.tree}>
        <span className={styles.root}>{viewName}</span>
        {Array.from(tree.entries()).map(([root, children]) => (
          <div key={root}>
            <div className={styles.branch}>├── {entityDisplayName(root)}</div>
            {children.map((child, i) => (
              <div key={i} className={styles.branch} style={{ paddingLeft: '32px' }}>
                {i === children.length - 1 ? '└── ' : '├── '}{entityDisplayName(child)}
              </div>
            ))}
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className={styles.tree}>
      {Array.from(tree.entries()).map(([root, children]) => (
        <div key={root}>
          <span className={styles.root}>{entityDisplayName(root)}</span>
          {children.map((child, i) => (
            <div key={i} className={styles.branch}>
              {i === children.length - 1 ? '└── ' : '├── '}{entityDisplayName(child)}
            </div>
          ))}
        </div>
      ))}
    </div>
  );
};

// ─── Combined wrapper ────────────────────────────────────────────────────────

interface RelationshipsSectionProps {
  viewLevelRelationships: ViewRelationshipItem[];
  datasetRelationships: DataModelRelationship[];
  baseTables: string[];
  isCrossDomain: boolean;
  viewName: string;
  entityDisplayName: (name: string) => string;
}

/** Renders the best available relationship tree, or an empty state. */
const RelationshipsSection: React.FC<RelationshipsSectionProps> = ({
  viewLevelRelationships,
  datasetRelationships,
  baseTables,
  isCrossDomain,
  viewName,
  entityDisplayName,
}) => {
  const styles = useStyles();

  if (viewLevelRelationships.length > 0) {
    return (
      <ViewRelationshipTreeComponent
        relationships={viewLevelRelationships}
        currentViewName={viewName}
        entityDisplayName={entityDisplayName}
      />
    );
  }

  if (datasetRelationships.length > 0) {
    return (
      <DatasetRelationshipTree
        relationships={datasetRelationships}
        baseTables={baseTables}
        isCrossDomain={isCrossDomain}
        viewName={viewName}
        entityDisplayName={entityDisplayName}
      />
    );
  }

  return <div className={styles.emptyState}>No relationships found for this view.</div>;
};

export default RelationshipsSection;